import React, { useEffect, useState } from "react";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { AntButton } from "components/AntButton";
import { useEstimatedGasFee } from "hook/useEstimatedGasFee";
import { useClaimStaking } from "hook/useClaimStaking";
import { formatDateTime } from "utils/date";
import { Container, ContainerView } from "../../components/Container";
import { TitlePage } from "../../components/Container";
import ArrowLeftIcon from "../../components/Icons/ArrowLeftIcon";
import { useApp } from "../../contexts";
import { PolygonIcon } from "../../components/Icons/PolygonIcon";
import { AIAIcon } from "../../components/Icons/AIAIcon";
import { SGTIcon } from "../../components/Icons/SGTIcon";
import { LabelValue } from "../../components/LabelValue";
import NumberFormat from "react-number-format";
import {
  decimalSeparatorByCurrency,
  thousandSeparatorByCurrency,
} from "../../utils/Currency";
import { getStakeTransactionDetail } from "../../services/app";
import * as Notification from "../../Notification";

export const ClaimStakingRequest = () => {
  const { T, getCurrency, loading } = useApp();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [stakeData, setStakeData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const { gasFee } = useEstimatedGasFee();

  const handleClickContinue = useClaimStaking({
    stakeRecordId: stakeData?.stakeRecordId,
    onSuccess: async () => {
      // Navigate to success page with transaction ID in URL
      if (stakeData?.stakeRecordId) {
        navigate(`/claim-stake-success?stakeRecordId=${stakeData?.stakeRecordId}`);
      } else {
        return;
      }
    },
  });

  useEffect(() => {
    const fetchStakeDetail = async () => {
      setIsLoading(true);

      const searchParams = new URLSearchParams(location.search);
      const stakeRecordId = searchParams.get("stakeRecordId");

      if (stakeRecordId) {
        const res = await getStakeTransactionDetail(stakeRecordId);
        if (res.status === 200 && res?.data) {
          setStakeData(res.data);
        } else {
          Notification.error(T("Failed to fetch stake detail"));
          console.error("Error fetching stake detail:", res.data);
        }
      }

      setIsLoading(false);
    };

    fetchStakeDetail();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  // Support LINE method callback (after user connects LINE in verification modal)
  useEffect(() => {
    if (searchParams.get("line_connected")) {
      handleClickContinue({
        chooseMethod: true,
      });
      setSearchParams(
        (prev) => {
          const newParams = new URLSearchParams(prev);
          newParams.delete("line_connected");
          return newParams;
        },
        {
          replace: true,
        }
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, setSearchParams]);

  const onBack = () => {
    navigate(-1);
  };

  if (!stakeData && !isLoading && !loading) {
    return (
      <Container>
        <ContainerView>
          <div className="text-center py-10">
            <p>{T("Stake information not found")}</p>
            <button
              onClick={onBack}
              className="mt-4 px-4 py-2 border border-black rounded-[15px]"
            >
              {T("Back")}
            </button>
          </div>
        </ContainerView>
      </Container>
    );
  }

  // Map stakeData to claimData format
  const claimData = {
    network: "Polygon Main Network",
    toAddress: "AI AVATAR Web3 Wallet Address",
    claimAmount: stakeData?.claimableAmount || stakeData?.amount || 0,
    claimDate: formatDateTime(stakeData?.claimTime),
    gasFee: stakeData?.gasFee || 0,
    gasFeeCurrency: "POL",
  };

  return (
    <Container>
      <div className="bg-[#f5f5f5] min-h-screen">
        <ContainerView>
          <TitlePage className="flex gap-1 items-center">
            <button
              className="text-[30px] lg:text-[42px] cursor-pointer"
              onClick={onBack}
            >
              <ArrowLeftIcon />
            </button>
            {T("Claim request")}
          </TitlePage>

          <div className="flex justify-center mt-[36px] mb-[50px]">
            <div className="bg-white rounded-[20px] p-[50px] w-full max-w-[784px]">
              <div className="flex flex-col gap-5 description-list">
                {/* Network */}
                <LabelValue
                  label={T("Network")}
                  value={
                    <div className="flex items-center gap-[5px]">
                      <PolygonIcon width={25} height={25} />
                      <div className="text-[16px] text-black leading-[25px]">
                        {T(claimData.network)}
                      </div>
                    </div>
                  }
                />

                {/* To Address */}
                <LabelValue
                  label={T("To Address")}
                  value={
                    <div className="flex items-center gap-[5px]">
                      <AIAIcon width={24} height={22} />
                      <p className="text-[16px] text-black leading-[25px]">
                        {T(claimData.toAddress)}
                      </p>
                    </div>
                  }
                />

                {/* Claim Amount */}
                <LabelValue
                  label={T("Claim Amount")}
                  value={
                    <div className="flex items-center gap-[8px]">
                      <SGTIcon width={24} height={24} />
                      <p className="text-[16px] text-black leading-[25px]">
                        <NumberFormat
                          value={claimData.claimAmount}
                          displayType="text"
                          thousandSeparator={thousandSeparatorByCurrency(
                            getCurrency()
                          )}
                          decimalSeparator={decimalSeparatorByCurrency(
                            getCurrency()
                          )}
                          decimalScale={0}
                          suffix=" SGT"
                        />
                      </p>
                    </div>
                  }
                />

                {/* Gas Fee */}
                <LabelValue
                  label={T("Gas Fee")}
                  value={
                    <div className="flex items-center gap-[5px]">
                      <PolygonIcon width={25} height={25} />
                      <p className="text-[16px] text-black leading-[normal]">
                        <NumberFormat
                          value={gasFee ?? 0}
                          displayType="text"
                          thousandSeparator={thousandSeparatorByCurrency(
                            getCurrency()
                          )}
                          decimalSeparator={decimalSeparatorByCurrency(
                            getCurrency()
                          )}
                          decimalScale={8}
                          suffix={` ${claimData.gasFeeCurrency}`}
                        />
                      </p>
                    </div>
                  }
                />
              </div>

              {/* Action Buttons */}
              <div className="flex justify-center gap-4 flex-row-reverse mt-5 md:mt-[50px]">
                <AntButton
                  type="button"
                  variant="primary"
                  onClick={handleClickContinue}
                >
                  {T("Continue")}
                </AntButton>
                <AntButton
                  type="button"
                  variant="outline"
                  onClick={onBack}
                >
                  {T("Back")}
                </AntButton>
              </div>
            </div>
          </div>
        </ContainerView>
      </div>
    </Container>
  );
};
